import { ActivityCardItem } from '@insify/design-system/components';
import clsx from 'clsx';

import { useActivity } from '../useActivity';

import * as styles from './index.module.css';

import type React from 'react';
import type { ChangeEventHandler } from 'react';

const ActivityCheckbox = ({
  idPrefix = '',
  id,
  suggestedTagText,
  onChange,
  showInclusionsExclusions = true,
  onDetailsClick,
  className,
}: {
  idPrefix?: string;
  id: string;
  suggestedTagText?: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  showInclusionsExclusions?: boolean;
  onDetailsClick: () => void;
  className?: string;
}) => {
  const { state } = useActivity();
  const activity = state.activityById[id];
  const { title: heading, subtitle: description, isChecked } = activity;

  return (
    <ActivityCardItem
      error={state.error}
      className={clsx(styles.item, className)}
      header={heading}
      isSuggested={state.registrySuggested.includes(id)}
      description={description}
      suggestedTagText={suggestedTagText}
      onDetailsClick={
        showInclusionsExclusions && (activity.inclusions?.length || activity.exclusions?.length)
          ? onDetailsClick
          : undefined
      }
      name={`${idPrefix}${id}`}
      checked={isChecked}
      onChange={onChange}
      id={id}
    />
  );
};

export const ActivitiesList = ({
  idPrefix,
  items,
  translations,
  onSelect,
  onDeselect,
  showInclusionsExclusions = true,
  onDetailsClick,
  itemClassName,
}: {
  idPrefix?: string;
  items: string[];
  translations: { suggested?: string };
  onSelect: (id: string) => void;
  onDeselect: (id: string) => void;
  showInclusionsExclusions?: boolean;
  onDetailsClick: (id: string) => void;
  itemClassName?: string | ((id: string) => string | undefined);
}) => {
  // Get className based on id if itemClassName is a function
  const getClassName = (id: string): string | undefined => {
    if (typeof itemClassName === 'function') {
      return itemClassName(id);
    }
    return itemClassName;
  };

  return (
    <ul>
      {items.map((id) => (
        <ActivityCheckbox
          key={id}
          idPrefix={idPrefix}
          id={id}
          suggestedTagText={translations.suggested}
          onChange={(e) => (e.target.checked ? onSelect(id) : onDeselect(id))}
          showInclusionsExclusions={showInclusionsExclusions}
          onDetailsClick={() => onDetailsClick(id)}
          className={getClassName(id)}
        />
      ))}
    </ul>
  );
};

type ListWrapperProps = {
  children: React.ReactNode;
  className?: string;
  hasMarginTop?: boolean;
};

export const ListWrapper = ({ children, hasMarginTop = true, className }: ListWrapperProps) => {
  return (
    <div
      className={clsx(styles.listWrapper, className, {
        [styles.listWrapperWithMargin]: hasMarginTop,
      })}
    >
      {children}
    </div>
  );
};
